import React, { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import cn from 'classnames'
import { Dropdown, Modal, Spin, message } from 'antd'
import requests from '../../../axios/requests'
import { setSelectedDocument, setSelectedTag } from '../../../../redux/actions/actions'
import useTrajectory from './useTrajectory'
import { pluralN } from './genUtils'
import { TYPE_TITLE, TYPE_ORDER } from './TestQuestion'
import s from './gen.module.scss'
import c from './courses.module.scss'
import t from './testgen.module.scss'
const COVERS = ['#1F2A44', '#14352B', '#2B2414', '#2A1F3D', '#1E3340']
const asList = (data) => (Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : [])
const docName = (doc) => doc?.file_name || doc?.name || doc?.title || 'Документ'
// статус для преподавателя: материалы ещё пишутся / черновик / опубликован
const statusOf = (course) => {
  if (course?.generation_in_progress) return 'gen'
  if (course?.isEnabled) return 'published'
  return 'draft'
}
const STATUS = {
  gen: { label: 'Генерируются материалы', cls: c.stGo },
  draft: { label: 'Черновик', cls: c.stNew },
  published: { label: '✓ Опубликован', cls: c.stDone },
}
const MoreIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <circle cx="5" cy="12" r="2" />
    <circle cx="12" cy="12" r="2" />
    <circle cx="19" cy="12" r="2" />
  </svg>
)
// Шаг 3 «Созданные»: курсы из планов и тесты из вариантов теста (по навыку и по документу).
const GeneratedCourses = () => {
  const history = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const { list } = useTrajectory()
  const [plans, setPlans] = useState(null)
  const [tests, setTests] = useState([])
  const [courses, setCourses] = useState([])
  const [docs, setDocs] = useState([])
  const [loading, setLoading] = useState(true)
  const [status, setStatus] = useState('all')
  const [type, setType] = useState('all')
  const [q, setQ] = useState('')
  const [created, setCreated] = useState(location.state?.createdTest || null)
  const [attachFor, setAttachFor] = useState(null)
  const [attachTo, setAttachTo] = useState(null)
  const [attaching, setAttaching] = useState(false)
  useEffect(() => {
    let alive = true
    Promise.all([
      requests.users.get_plannings().then((r) => asList(r.data)),
      requests.users
        .get_education_plan_pdf_list()
        .then((r) => asList(r.data))
        .catch(() => []),
      requests.quests.get_quest_pagination(0, '', 1, 200).then((r) => asList(r.data)),
      requests.users
        .get_speciality_pdf_list()
        .then((r) => asList(r.data))
        .catch(() => []),
      requests.users
        .quiz_drafts({ created: 1 })
        .then((r) => asList(r.data))
        .catch(() => []),
    ])
      .then(([pl, pdfPl, qs, ds, ts]) => {
        if (!alive) return
        const seen = new Set()
        setPlans(
          [...pl, ...pdfPl.map((x) => ({ ...x, fromDoc: true }))].filter((x) => {
            if (!x.quest?.id || seen.has(x.quest.id)) return false
            seen.add(x.quest.id)
            return true
          })
        )
        setTests(ts.filter((x) => x.quest?.id))
        setCourses(qs)
        setDocs(ds)
      })
      .catch((err) => console.error(err))
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
  }, [])
  const items = useMemo(() => {
    if (!plans) return []
    const byId = {}
    courses.forEach((x) => (byId[x.id] = x))
    const source = (x) => {
      if (x.fromDoc || x.pdf) {
        const doc = docs.find((d) => String(d.id) === String(x.pdf))
        return `Документ · ${doc ? docName(doc) : 'загруженный файл'}`
      }
      const skill = list.find((sk) => sk.id === x.speciality_tag)?.name
      return skill ? `Навык · ${skill}` : ''
    }
    const testQuestIds = new Set(tests.map((x) => x.quest.id))
    return [
      ...plans
        .filter((pl) => !testQuestIds.has(pl.quest.id))
        .map((pl) => ({
          kind: 'course',
          key: `p${pl.id}`,
          plan: pl,
          quest: pl.quest,
          course: byId[pl.quest.id] || null,
          source: source(pl),
          at: pl.created_at,
        })),
      ...tests.map((d) => ({
        kind: 'test',
        key: `t${d.id}`,
        draft: d,
        quest: d.quest,
        course: byId[d.quest.id] || null,
        source: source(d),
        at: d.created_at,
      })),
    ].sort((a, b) => (b.at || '').localeCompare(a.at || ''))
  }, [plans, tests, courses, docs, list])
  const ofType = items.filter((it) => type === 'all' || it.kind === type)
  const counts = { all: ofType.length, gen: 0, draft: 0, published: 0 }
  ofType.forEach((it) => (counts[statusOf(it.course || it.quest)] += 1))
  const query = q.trim().toLowerCase()
  const shown = ofType
    .filter((it) => status === 'all' || statusOf(it.course || it.quest) === status)
    .filter(
      (it) =>
        !query ||
        (it.course?.name || it.quest?.name || '').toLowerCase().includes(query) ||
        (it.source || '').toLowerCase().includes(query)
    )
  const ses = (it) => it.course?.ses || it.quest.ses
  const edit = (it) => history(`/backoffice/current-course/${ses(it)}`)
  const preview = (it) => history(`/backoffice/course-preview/${ses(it)}`)
  const testFor = (it) => {
    const skill = list.find((sk) => sk.id === it.plan?.speciality_tag)
    if (skill) {
      dispatch(setSelectedDocument(null))
      dispatch(setSelectedTag(skill))
    }
    history('/backoffice/skill-set/generate-course', { state: { kind: 'test' } })
  }
  const attachTargets = courses.filter((x) => !tests.some((d) => d.quest.id === x.id))
  const attach = () => {
    if (!attachFor || !attachTo) return
    setAttaching(true)
    requests.users
      .quiz_draft_action(attachFor.id, 'attach', { quest_id: attachTo })
      .then(({ data }) => {
        message.success(`Тест добавлен последним занятием в курс «${data.quest?.name}»`)
        setAttachFor(null)
        setAttachTo(null)
      })
      .catch((err) => message.error(err?.response?.data?.[0] || 'Не удалось вставить тест'))
      .finally(() => setAttaching(false))
  }
  const typesOf = (d) =>
    TYPE_ORDER.filter((k) => d.type_counts?.[k] > 0)
      .map((k) => TYPE_TITLE[k].toLowerCase())
      .join(', ')
  return (
    <section className={s.stage} aria-label="Созданные курсы и тесты">
      {created?.quest && (
        <div className={t.banner} role="status">
          <span className={t.bannerIco}>
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              aria-hidden="true"
            >
              <path d="M5 12l5 5L20 7" />
            </svg>
          </span>
          <div style={{ flex: '1 1 260px' }}>
            <div style={{ fontWeight: 600 }}>Тест «{created.quest.name}» создан</div>
            <div className={s.small}>
              {pluralN((created.questions || []).length, ['вопрос', 'вопроса', 'вопросов'])} в черновике. Задайте
              проходной балл и опубликуйте — или вставьте в курс как итоговое занятие.
            </div>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <button type="button" className={cn(s.btn, s.sec)} onClick={() => setAttachFor(created)}>
              Вставить в курс…
            </button>
            <button
              type="button"
              className={cn(s.btn, s.pri)}
              onClick={() => history(`/backoffice/current-course/${created.quest.ses}`)}
            >
              Открыть в редакторе
            </button>
            <button
              type="button"
              className={cn(s.btn, s.ghost, s.icon)}
              aria-label="Скрыть"
              onClick={() => setCreated(null)}
            >
              ×
            </button>
          </div>
        </div>
      )}
      <div className={s.stagehead}>
        <div>
          <div className={s.kicker}>Шаг 3 из 3</div>
          <h2>Созданные курсы и тесты</h2>
          <div className={cn(s.muted, s.small)}>
            Материалы курсов дописываются в фоне. Тест можно опубликовать отдельно или вставить в курс.
          </div>
        </div>
      </div>
      <div className={c.toolbar}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          <div className={s.seg} role="radiogroup" aria-label="Тип">
            {[
              ['all', 'Все', items.length],
              ['course', 'Курсы', items.filter((x) => x.kind === 'course').length],
              ['test', 'Тесты', items.filter((x) => x.kind === 'test').length],
            ].map(([key, label, n]) => (
              <button
                type="button"
                key={key}
                role="radio"
                aria-checked={type === key}
                className={cn({ [s.segOn]: type === key })}
                onClick={() => setType(key)}
              >
                {label} · {n}
              </button>
            ))}
          </div>
          <div className={s.seg} role="radiogroup" aria-label="Статус">
            {[
              ['all', 'Любой статус'],
              ['gen', 'Генерируются'],
              ['draft', 'Черновики'],
              ['published', 'Опубликованы'],
            ].map(([key, label]) => (
              <button
                type="button"
                key={key}
                role="radio"
                aria-checked={status === key}
                className={cn({ [s.segOn]: status === key })}
                onClick={() => setStatus(key)}
              >
                {label}
                {key !== 'all' ? ` · ${counts[key]}` : ''}
              </button>
            ))}
          </div>
        </div>
        <div className={c.toolbarRight}>
          <label className={s.field} style={{ flex: 1 }}>
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="7" />
              <path d="M20 20l-3.5-3.5" />
            </svg>
            <input
              placeholder="Поиск по названию"
              aria-label="Поиск по курсам и тестам"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </label>
          <button
            type="button"
            className={cn(s.btn, s.pri)}
            onClick={() => history('/backoffice/skill-set/generate-course')}
          >
            + Сгенерировать
          </button>
        </div>
      </div>
      {loading ? (
        <Spin style={{ margin: '40px auto' }} />
      ) : !items.length ? (
        <div className={cn(s.card, s.empty)}>
          <h3 style={{ marginBottom: 6 }}>Здесь появятся курсы и тесты, созданные ИИ</h3>
          <div style={{ marginBottom: 14 }}>
            Выберите навык или загрузите документ на шаге 2 — курс или тест соберётся за пару минут
          </div>
          <button
            type="button"
            className={cn(s.btn, s.pri)}
            onClick={() => history('/backoffice/skill-set/generate-course')}
          >
            Перейти к генерации
          </button>
        </div>
      ) : !shown.length ? (
        <div className={cn(s.card, s.empty)}>Ничего не нашлось</div>
      ) : (
        <div className={c.grid}>
          {shown.map((it, i) => {
            const course = it.course || {}
            const st = STATUS[statusOf(it.course || it.quest)]
            if (it.kind === 'test') {
              const d = it.draft
              const n = (d.questions || []).length
              const passes = d.quest.passes || 0
              return (
                <article key={it.key} className={c.course}>
                  <div className={cn(c.cover, t.testCover)}>
                    <div>
                      <div className={t.bigNum}>{n}</div>
                      <div className={s.small}>
                        {pluralN(n, ['вопрос', 'вопроса', 'вопросов']).replace(/^\d+ /, '')}
                        {passes ? ` · ${pluralN(passes, ['прохождение', 'прохождения', 'прохождений'])}` : ''}
                      </div>
                    </div>
                    <span className={c.kind}>Тест</span>
                  </div>
                  <div className={c.body}>
                    <div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 4 }}>
                        <span className={cn(c.st, st.cls)}>{st.label}</span>
                        {it.source && <span className={cn(s.muted, s.small)}>{it.source}</span>}
                      </div>
                      <h3>{course.name || d.quest.name}</h3>
                    </div>
                    <div className={c.cnt}>
                      <span>{typesOf(d)}</span>
                      <span>~{d.minutes} мин</span>
                    </div>
                    <div className={c.actions}>
                      <button type="button" className={cn(s.btn, s.pri)} style={{ flex: 1 }} onClick={() => edit(it)}>
                        Открыть
                      </button>
                      <Dropdown
                        trigger={['click']}
                        menu={{
                          items: [
                            { key: 'preview', label: 'Предпросмотр', onClick: () => preview(it) },
                            { key: 'attach', label: 'Вставить в курс…', onClick: () => setAttachFor(d) },
                          ],
                        }}
                      >
                        <button type="button" className={cn(s.btn, s.sec, s.icon)} aria-label="Ещё действия">
                          <MoreIcon />
                        </button>
                      </Dropdown>
                    </div>
                  </div>
                </article>
              )
            }
            const img = course.y_image || course.banner
            return (
              <article key={it.key} className={c.course}>
                <div
                  className={c.cover}
                  style={img ? { backgroundImage: `url(${img})` } : { background: COVERS[i % COVERS.length] }}
                >
                  <span className={cn(c.st, st.cls)}>{st.label}</span>
                  <span className={c.kind}>Курс</span>
                </div>
                <div className={c.body}>
                  <div>
                    {it.source && <div className={cn(s.muted, s.small)}>{it.source}</div>}
                    <h3>{course.name || 'Курс без названия'}</h3>
                  </div>
                  <div className={c.cnt}>
                    {course.count_video > 0 && (
                      <span>
                        {'▶︎'} {pluralN(course.count_video, ['видео', 'видео', 'видео'])}
                      </span>
                    )}
                    {course.count_article > 0 && (
                      <span>▤ {pluralN(course.count_article, ['статья', 'статьи', 'статей'])}</span>
                    )}
                    {course.count_quiz > 0 && <span>✓ {pluralN(course.count_quiz, ['тест', 'теста', 'тестов'])}</span>}
                    {course.count_task > 0 && (
                      <span>✎ {pluralN(course.count_task, ['задание', 'задания', 'заданий'])}</span>
                    )}
                    {course.count_scorm_task > 0 && <span>SCORM</span>}
                  </div>
                  <div className={c.actions}>
                    <button type="button" className={cn(s.btn, s.pri)} style={{ flex: 1 }} onClick={() => edit(it)}>
                      Редактировать
                    </button>
                    <Dropdown
                      trigger={['click']}
                      menu={{
                        items: [
                          { key: 'preview', label: 'Предпросмотр', onClick: () => preview(it) },
                          { key: 'test', label: '+ Итоговый тест', onClick: () => testFor(it) },
                        ],
                      }}
                    >
                      <button type="button" className={cn(s.btn, s.sec, s.icon)} aria-label="Ещё действия">
                        <MoreIcon />
                      </button>
                    </Dropdown>
                  </div>
                </div>
              </article>
            )
          })}
        </div>
      )}
      <Modal
        open={!!attachFor}
        onCancel={() => setAttachFor(null)}
        onOk={attach}
        okText="Вставить"
        cancelText="Отмена"
        okButtonProps={{ disabled: !attachTo, loading: attaching }}
        title="Вставить тест в курс"
        destroyOnClose
      >
        <p style={{ marginBottom: 12 }}>
          Копия теста станет последним занятием «Итоговый тест» выбранного курса. Сам тест останется отдельным.
        </p>
        {attachTargets.length ? (
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 320, overflowY: 'auto' }}
            role="radiogroup"
          >
            {attachTargets.map((x) => (
              <label
                key={x.id}
                style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer', padding: '6px 4px' }}
              >
                <input
                  type="radio"
                  name="attach-course"
                  checked={attachTo === x.id}
                  onChange={() => setAttachTo(x.id)}
                />
                <span>{x.name}</span>
              </label>
            ))}
          </div>
        ) : (
          <p>У вас пока нет курсов, куда можно вставить тест.</p>
        )}
      </Modal>
    </section>
  )
}
export default GeneratedCourses
